import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  Avatar,
  Checkbox,
  Dialog,
  Chip,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Grid
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { Visibility } from "@mui/icons-material";
import Icon from "../../../../components/icon";
import { GetEmployees } from "screens/Employees/logic/GetEmployees";
import { GetInvitationHistory } from "screens/Employees/logic/GetInvitationHistory";
import { filterDataOnboardingInvite } from "screens/Employees/logic/filterData";
import { useNavigate } from "react-router-dom";
import { SendInvite } from "screens/Auth/invite/logic/InviteHandler";
import InvitationSent from "images/InvitationSent.jpg";
import { ErrorIcon } from "react-hot-toast";
import { useTheme, useMediaQuery } from "@mui/material";
// import { GetInvitationHistory } from "screens/Employees/logic/GetInvitationHistory";

const StepInviteUsers = ({ handlePrev, handleNext, handleNextInviteUsers }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [roleFilter, setRoleFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [invitedEmails, setInvitedEmails] = useState(new Set());
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [result, setResult] = useState([]);
  const [showErrorListDialog, setShowErrorListDialog] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  // Check if an employee is already invited
  const isEmployeeInvited = (email) => {
    return invitedEmails.has(email?.toLowerCase());
  };

  const toggleUser = (userId) => {
    const user = users.find(u => u.id === userId);
    // Prevent toggling if already invited
    if (user && isEmployeeInvited(user.email)) {
      return;
    }
    
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleAllUsers = () => {
    // Filter out already invited users
    const selectableUsers = filteredUsers.filter(user => !isEmployeeInvited(user.email));
    
    if (selectedUsers.length === selectableUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(selectableUsers.map((user) => user.id));
    }
  };

  const keysToKeep = ["employeeID", "firstName", "email", "roleName"];

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      let emp = await GetEmployees(0, navigate);
      const filteredDataNew = filterDataOnboardingInvite(emp, keysToKeep);
      setUsers(filteredDataNew);
      
      // Fetch invitation history and extract invited emails
      const invitations = await GetInvitationHistory(0, navigate);
      if (invitations && !invitations.isError && invitations.result) {
        // Create a Set of invited emails (case-insensitive)
        const invitedEmailsSet = new Set(
          invitations.result.map(invitation => invitation.email.toLowerCase())
        );
        setInvitedEmails(invitedEmailsSet);
      }
    } catch (error) {
      console.error("Error fetching employee data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  function getInitials(fullName) {
  const names = fullName.split(' ');
  if (names.length === 1) return names[0][0].toUpperCase();
  return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
}
  const inviteAndNext = async () => {
    try {
      setLoading(true);
      if (selectedUsers.length > 0) {
        const results = await Promise.all(
          selectedUsers.map(async (userId) => {
            try {
              const res = await SendInvite(userId, navigate);
              return {
                userId,
                success: !res.isError,
                message: res.message,
                userId: userId,
                fullName: getEmployeeByID(userId)?.Fullname || "Unknown User",
                email: getEmployeeByID(userId)?.email || "Unknown Email"
              };
            } catch (error) {
              return { userId, success: false };
            }
          })
        );

        const allSuccess = results.every((res) => res.success);
        const successfulInvites = results.filter(res => res.success);
        
        // Refetch invitation history if any invites were successful
        if (successfulInvites.length > 0) {
          const invitations = await GetInvitationHistory(0, navigate);
          if (invitations && !invitations.isError && invitations.result) {
            const invitedEmailsSet = new Set(
              invitations.result.map(invitation => invitation.email.toLowerCase())
            );
            setInvitedEmails(invitedEmailsSet);
          }
          // Clear selection for successfully invited users
          setSelectedUsers(prev => prev.filter(userId => {
            const user = getEmployeeByID(userId);
            return !successfulInvites.some(inv => inv.userId === userId);
          }));
        }
        
        if (allSuccess) {
          setResult([]);
          setShowConfirmationDialog(true);           
        } else {
          setResult(results.filter((res) => !res.success));
          if(successfulInvites.length > 0) {
            setShowConfirmationDialog(true); // Show dialog even if some invites failed
          }else{
            setShowErrorListDialog(true); // Show error list dialog if all invites failed
          }
        }
      }
    } catch (error) {
      console.error("Error in inviteAndNext:", error);
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeByID = (employeeID) => {
    return users.find((user) => user.id === employeeID);
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.Fullname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === "all" || user.roleName === roleFilter;
    return matchesSearch && matchesRole;
  });

  const columns = [
    {
      field: "select",
      headerName: "",
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => {
        const selectableUsers = filteredUsers.filter(user => !isEmployeeInvited(user.email));
        return (
          <Checkbox
            size="small"
            color="success"
            checked={
              selectedUsers.length > 0 &&
              selectedUsers.length === selectableUsers.length &&
              selectableUsers.length > 0
            }
            onChange={toggleAllUsers}
          />
        );
      },
      renderCell: (params) => {
        const isInvited = isEmployeeInvited(params.row.email);
        return (
          <Checkbox
            size="small"
            color="success"
            checked={selectedUsers.includes(params.row.id)}
            onChange={() => toggleUser(params.row.id)}
            disabled={isInvited}
          />
        );
      }
    },
    {
      field: "Fullname",
      headerName: "Name",
      width: 250,
      renderCell: (params) => (
        <Typography variant="body2">{params.value}</Typography>
      )
    },
    {
      field: "invited",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const isInvited = isEmployeeInvited(params.row.email);
        return isInvited ? (
          <Tooltip title="Already Invited" arrow>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Icon icon="tabler:mail-check" style={{ color: '#28a745', fontSize: '20px' }} />
            </Box>
          </Tooltip>
        ) : null;
      }
    },
    // {
    //   field: "Fullname",
    //   headerName: "Name",
    //   width: 250,
    //   renderCell: (params) => {
    //     const isInvited = isEmployeeInvited(params.row.email);
    //     return (
    //       <>
    //         <Typography variant="body2">{params.value}</Typography>
    
    //         {isInvited ? (
    //           <Tooltip title="Already Invited" arrow>
    //             <Box sx={{ display: 'flex', alignItems: 'center' }}>
    //               <Icon
    //                 icon="tabler:mail-check"
    //                 style={{ color: '#28a745', fontSize: '20px' }}
    //               />
    //             </Box>
    //           </Tooltip>
    //         ) : null}
    //       </>
    //     );
    //   }
    // },    
    {
      field: "email",
      headerName: "Email",
      width: 260,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value}
        </Typography>
      )
    },
    {
      field: "roleName",
      headerName: "Role",
      width: 240,
      renderCell: (params) => (
        <Typography variant="body2">{params.value}</Typography>
      )
    }
  ];

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        px: 2,
        py: 3,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: "850px" }}>
        <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        mb: 6,
        pt: 0,
        pl: { xs: 0, sm: "280px" }, // Remove left padding on mobile
        flexDirection: { xs: "column", sm: "row" }, // Stack on mobile
        textAlign: { xs: "center", sm: "left" } // Center text on mobile
      }}
    >
         <Avatar
        sx={{
          bgcolor: "transparent",
          border: "1px solid #e0e0e0",
          color: "text.primary",
          mr: { xs: 0, sm: 2 }, 
          mb: { xs: 2, sm: 0 }, 
          height: { xs: 40, sm: 48 }, 
          width: { xs: 40, sm: 48 }, 
          alignSelf: { xs: "center", sm: "flex-start" } 
        }}
      >
            <Icon icon="solar:user-broken" />
          </Avatar>
         <Box sx={{ textAlign: { xs: "center", sm: "left" } }}> 
        <Typography variant="h5" sx={{ mb: 0, fontSize: { xs: "1.24rem", sm: "1.24rem" } }}>
          Invite Users
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, textAlign: { xs: "center", sm: "left" }, fontSize: { xs: "0.875rem", sm: "0.9rem" } }}
        >
          Select employees to invite to your Client workspace
        </Typography>
      </Box>
        </Box>

        {/* Search & Filter */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            justifyContent: "space-between",
            mb: 3,
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder="Search Employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="disabled" fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: "300px" }}
          />

          <Box sx={{ display: "flex", gap: 2 }}>
           <FormControl size="small" sx={{ width: "148px" }}>
            <Select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              displayEmpty
              renderValue={(selected) =>
                selected === "all" ? "Filter By" : selected
              }
              sx={{
                fontSize: "0.875rem",
                "& .MuiSelect-select": {
                  padding: "8px 12px",
                },
              }}
            >
              <MenuItem value="all">All Roles</MenuItem>
              {[
                { value: "Admin", label: "Admin" },
                { value: "CrewLeader", label: "Crew Leader" },
                { value: "CrewMember", label: "Crew Member" },
                { value: "OfficeManager", label: "Office Manager" },
                { value: "Owner", label: "Owner" },
                { value: "Subcontracter", label: "Subcontractor" }
              ]
                .sort((a, b) => a.label.localeCompare(b.label))
                .map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
            <Button
              variant="contained"
              color="main"
              disabled={selectedUsers.length === 0}
              onClick={inviteAndNext}
            >
              Send Invite
            </Button>
          </Box>
        </Box>

        {/* DataGrid */}
        <Box sx={{ height: 300, width: "100%", mb: 3 }}>
          <DataGridPro
            rows={filteredUsers}
            columns={columns}
            scrollable
            disableRowSelectionOnClick
            hideFooter
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            sx={{
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid",
                borderColor: "divider",
                py: 1,
                display: "flex",
                alignItems: "center",
              },
              "& .MuiDataGrid-row": {
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              },
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                textTransform: "none",
              },
              "& .MuiDataGrid-virtualScroller": {
                overflowX: "visible",
              },
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "none",
              },
            }}
            getRowHeight={() => "auto"}
            columnHeaderHeight={44}
            rowHeight={44}
          />
        </Box>

        {/* Navigation Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            pt: 2,
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            sx={{
              textTransform: "none",
              borderColor: "transparent",
              color: "text.secondary",
            }}
            onClick={handlePrev}
          >
            Previous
          </Button>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="text"
              color="secondary"
              size="small"
              onClick={() => handleNext([])}
            >
              Skip for Now
            </Button>
            <Button
              variant="contained"
              color="main"
              // onClick={inviteAndNext}
              // disabled={selectedUsers.length === 0}
              onClick={() => handleNext([])}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ✅ Confirmation Dialog */}
      <Dialog
        open={showConfirmationDialog}
        onClose={() => setShowConfirmationDialog(false)}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            textAlign: "center",
            maxWidth: 480,
            px: 3,
            py: 2,
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "flex-end", p: 0 }}>
          <IconButton onClick={() => setShowConfirmationDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <img src={InvitationSent} />
          </Box>
          <Typography variant="h5">Invite Sent Successfully</Typography>
          <Typography variant="body1" color="text.secondary" mt={1}>
            Your invitation has been sent to the selected users.
            {result.length > 0 && (
              <Tooltip
                title="Click here to see the list of excluded users."
                arrow
              >
                <IconButton onClick={() => setShowErrorListDialog(true)}>
                  <Visibility />
                </IconButton>
              </Tooltip>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button
            onClick={() => {
              setShowConfirmationDialog(false);
              handleNextInviteUsers(selectedUsers);
            }}
            variant="contained"
            color="main"
            sx={{ width: "400%", maxWidth: 420, borderRadius: 2 }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error list Dialog */}
        <Dialog
      open={showErrorListDialog}
      onClose={() => setShowErrorListDialog(false)}
      fullWidth
   
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2, // remove rounded corners on mobile
          maxWidth: 600,
          p: 0,
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
          backgroundColor: "white",
          position: "relative",
        },
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={() => setShowErrorListDialog(false)}
        sx={{
          position: "absolute",
          right: 16,
          top: 16,
          color: "text.secondary",
          zIndex: 1,
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Header */}
      <Box
        sx={{
          p: 2,
          pt: 5,
          pb: 0,
          backgroundColor: "white",
        }}
      >
        <Typography
          variant={isMobile ? "h6" : "h4"} // smaller heading on mobile
          sx={{
            color: "text.primary",
            ml: 4,
            mb: 0.5,
          }}
        >
          Invitation Issues
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", ml: 4 }}
        >
          {result.length} users could not be invited
        </Typography>
      </Box>

      {/* Content */}
      <DialogContent
        sx={{
          backgroundColor: "white",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
              alignItems: isMobile ? "center" : "stretch",
          }}
        >
          {result.map((user, index) => (
            <Box
              key={index}
              sx={{
                p: 2.5,
                borderRadius: 1,
                border: "1px solid #f0f0f0",
                backgroundColor: "white",
                display: "flex",
                gap: 2,
                position: "relative",
                flexDirection: isMobile ? "column" : "row", // stack items on mobile
                alignItems: isMobile ? "center" : "center",
              }}
            >
              {/* Initials Circle */}
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  mt: isMobile ? 0 : 1,
                  borderRadius: "50%",
                  backgroundColor: "#FBF0D5CC",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 500,
                  color: "text.primary",
                  flexShrink: 0,
                }}
              >
                {getInitials(user.fullName)}
              </Box>

              {/* User Info */}
              <Box sx={{ flex: 1, pr: isMobile ? 4 : 8 ,textAlign: { xs: "center", sm: "left" } }}>
                <Typography
                  sx={{
                    fontWeight: "500",
                    color: "text.primary",
                  }}
                >
                  {user.fullName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary" }}
                >
                  {user.email}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary" }}
                >
                  An active invitation already exists for this email
                </Typography>
              </Box>

              {/* Tag */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  backgroundColor: "#FBF0D5CC",
                  borderRadius: "18px",
                  px: 2,
                  height: 24,
                  position: isMobile ? "static" : "absolute",
                  right: isMobile ? "auto" : 16,
                  top: isMobile ? "auto" : "50%",
                  transform: isMobile ? "none" : "translateY(-60%)",
                  mt: isMobile ? 1 : 0,
                }}
              >
                <Icon
                  icon="tabler:info-triangle"
                  width="16"
                  height="16"
                  style={{ color: "#D3912C", marginRight: 4 }}
                />
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "#D3912C",
                    fontWeight: 380,
                    lineHeight: 1,
                  }}
                >
                  Already Invited
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
    </Box>
  );
};

export default StepInviteUsers;
